<template>
  <aside class="basketDock" :style="{'--basketDockBottom': `${bottom}px`}" :aria-label="$t('BASKET')">
    <v-icon class="basketIcon" size="28" aria-hidden="true">shopping_cart</v-icon>
    <div class="basketSummary" aria-live="polite" aria-atomic="true">
      <div class="itemCount">{{ $t('BASKET_ITEM_COUNT').replace('{amount}', basket.positionCount) }}</div>
      <div class="subtotal">
        <span>{{ $t('BASKET_SUBTOTAL') }}:</span>
        <strong>{{ formatPrice(basket.subtotal) }}</strong>
      </div>
    </div>
    <v-btn color="primary" to="/basket" class="basketButton">{{ $t('GO_TO_BASKET') }}</v-btn>
  </aside>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import BasicComponent from '@/global/BasicComponent';
import {BasketDto} from '@swagger';
import {Prop, Watch} from 'vue-property-decorator';

@Component
export default class BasketDock extends BasicComponent {
  @Prop({default: null}) footerElement: HTMLElement | null;
  @Prop({default: null}) contentElement: HTMLElement | null;
  resizeObserver: ResizeObserver | null = null;
  positionFrame: number | null = null;
  bottom = 0;

  get basket(): BasketDto {
    return this.$store.getters.basket;
  }

  mounted() {
    // The dock reserves its actual height and follows a footer with variable content.
    this.resizeObserver = new ResizeObserver(this.schedulePositionUpdate);
    this.observeLayout();
    window.addEventListener('scroll', this.schedulePositionUpdate, {passive: true});
    window.addEventListener('resize', this.schedulePositionUpdate);
  }

  @Watch('footerElement')
  @Watch('contentElement')
  observeLayout() {
    this.resizeObserver?.disconnect();
    [this.$el, this.footerElement, this.contentElement].forEach(element => {
      if (element) {
        this.resizeObserver?.observe(element);
      }
    });
    this.schedulePositionUpdate();
  }

  schedulePositionUpdate() {
    if (this.positionFrame !== null) {
      return;
    }
    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = null;
      const style = window.getComputedStyle(this.$el);
      const edgeGap = parseFloat(style.getPropertyValue('--dock-edge-gap')) || 0;
      const footerGap = parseFloat(style.getPropertyValue('--dock-footer-gap')) || 0;
      const footerTop = this.footerElement?.getBoundingClientRect().top ?? window.innerHeight;
      this.bottom = Math.max(edgeGap, window.innerHeight - footerTop + footerGap);
      this.shopLayout.dockHeight = this.$el.getBoundingClientRect().height;
      this.shopLayout.dockTop = window.innerHeight - this.bottom - this.shopLayout.dockHeight;
    });
  }

  beforeDestroy() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.schedulePositionUpdate);
    window.removeEventListener('resize', this.schedulePositionUpdate);
    if (this.positionFrame !== null) {
      window.cancelAnimationFrame(this.positionFrame);
    }
    this.shopLayout.dockHeight = 0;
    this.shopLayout.dockTop = null;
  }
}
</script>

<style scoped lang="scss">
@use "../stylesheets/Variables.scss" as *;

.basketDock {
  --dock-edge-gap: 24px;
  --dock-footer-gap: 16px;
  position: fixed;
  bottom: var(--basketDockBottom, var(--dock-edge-gap));
  right: max(var(--dock-edge-gap), calc((100vw - 1920px) / 2 + var(--dock-edge-gap)));
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100% - var(--dock-edge-gap) - var(--dock-edge-gap));
  padding: 12px 16px;
  border: 1px solid $shop-border;
  border-radius: 6px;
  background: $shop-white;
  color: $shop-text;
  box-shadow: 0 3px 14px rgba($shop-black, .15);
}

.basketIcon {

  flex: 0 0 auto;

  color: inherit;

}
.basketSummary {
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
}
.subtotal {
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
}
.subtotal strong {
  white-space: nowrap;
  color: $shop-text-strong;
}
.basketButton {
  flex: 0 1 auto;
  min-width: 0 !important;
}
@media (max-width: 959px) {
  .basketDock {
    --dock-edge-gap: 0px;
    --dock-footer-gap: 0px;
    bottom: var(--basketDockBottom, var(--dock-edge-gap));
    left: 0;
    right: 0;
    width: 100%;
    max-width: none;
    gap: 8px;
    padding: 12px max(12px, env(safe-area-inset-right)) calc(12px + env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
    border: 0;
    border-top: 1px solid $shop-border;
    border-radius: 0;
    box-shadow: 0 -2px 8px rgba($shop-black, .06);
  }

  .basketSummary {

    flex: 1 1 auto;

    font-size: 12px;

  }
  .basketButton {
    max-width: 45%;
    height: auto !important;
    min-height: 40px;
    padding: 8px 12px !important;
    font-size: 12px;
    ::v-deep .v-btn__content {
      min-width: 0;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
}
</style>
